import { useEffect, useState } from "react";
import EventGallery from "@/pages/EventGallery";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, MapPin, Phone, Mail, ArrowRight, Sparkles, Calendar, Trophy, FileText } from "lucide-react";
import GlobalAppHeader from "@/components/shared/GlobalAppHeader";
import GlobalAppFooter from "@/components/shared/GlobalAppFooter";
import SingleLineScrollTrack from "@/components/landing/SingleLineScrollTrack";
import HeroMediaCarousel from "@/components/landing/HeroMediaCarousel";
import TabbedOnboarding from "@/components/landing/TabbedOnboarding";
import TabbedPlans from "@/components/landing/TabbedPlans";
import DiscoveryRails from "./landing_page/DiscoveryRails";
import { HorizontalPostCarousel, SubscribeCard } from "@/components/posts/PublicPosts";

const ENQUIRY_TYPES = [
  "School Partnership / ERP-LMS",
  "TV Channel / Evening Academy Creator",
  "Local Bookstore / Storez Vendor",
  "Open Student / Parent",
  "Franchise Opportunity (Turnkey Cluster)",
];


const FALLBACK_FEATURES = [
  {title:"Multi-tenant ERP", description:"Each school gets a fully isolated portal.", bg_color:"bg-[#93C5FD]", image_url:"https://images.unsplash.com/photo-1503676260728-1c00da094a0b"},
  {title:"Smart Fees", description:"Online + offline + partial. Auto receipts.", bg_color:"bg-[#86EFAC]", image_url:"https://images.pexels.com/photos/8617834/pexels-photo-8617834.jpeg"},
  {title:"Parent SMS", description:"Absence and dues alerts dispatched instantly.", bg_color:"bg-[#FDE047]", image_url:"https://images.unsplash.com/photo-1497486751825-1233686d5d80"},
  {title:"Ponderr LMS", description:"Curated content streamed to every grade.", bg_color:"bg-[#C4B5FD]", image_url:"https://images.pexels.com/photos/8926848/pexels-photo-8926848.jpeg"},
];

// Legacy exports kept for backward-compat (imported by other public pages).
export function GlobalHeader() { return <GlobalAppHeader />; }
export function GlobalFooter({ site }) { return <GlobalAppFooter site={site} />; }

export default function GlobalLanding() {
  const [site, setSite] = useState({});
  const [schools, setSchools] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [features, setFeatures] = useState([]);
  const [heroMedia, setHeroMedia] = useState([]);
  const [platformPosts, setPlatformPosts] = useState([]);
  const [contact, setContact] = useState({name:"", email:"", phone:"", message:"", enquiry_type:""});

  useEffect(()=>{
    api.get("/global/site").then(r=>setSite(r.data)).catch(()=>{});
    api.get("/global/schools/public").then(r=>setSchools(r.data)).catch(()=>{});
    api.get("/global/updates").then(r=>setUpdates((r.data||[]).filter(u=>u.show_on_landing!==false))).catch(()=>{});
    api.get("/global/features").then(r=>setFeatures(r.data)).catch(()=>{});
    api.get("/site-settings").then(r=>setHeroMedia(r.data.hero_media || [])).catch(()=>{});
    api.get("/posts/public?entity_type=platform&limit=8").then(r=>setPlatformPosts(r.data||[])).catch(()=>{});
  }, []);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    try { await api.post("/enquiries", contact); toast.success("Thanks! We'll reach out."); setContact({name:"",email:"",phone:"",message:"",enquiry_type:""}); }
    catch { toast.error("Failed"); }
  };
  const featList = features.length ? features : FALLBACK_FEATURES;

  return (
    <div className="min-h-screen pf-app max-w-full overflow-x-hidden">
      <GlobalAppHeader/>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 py-12 md:py-16 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-6 space-y-5">
          {site.hero_badge && <div className="nb-badge inline-block" data-testid="hero-badge">{site.hero_badge}</div>}
          <h1 className="display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]" data-testid="hero-title">
            {site.hero_title_main || "One platform for"} <span className="pf-accent-bg px-2 border-2 pf-border inline-block -rotate-1">{site.hero_title_highlight || "every learner"}</span>
          </h1>
          <p className="text-lg max-w-xl pf-muted">{site.hero_subtitle || "Schools, stores, TV and open learning — unified."}</p>
          <div className="flex flex-wrap gap-3">
            <a href="#onboarding" className="pf-btn" data-testid="hero-cta-primary">{site.hero_cta_primary || "Get Started"} <Sparkles size={16}/></a>
            <a href="#plans" className="pf-btn !bg-transparent" data-testid="hero-cta-secondary">{site.hero_cta_secondary || "See Plans"}</a>
          </div>
        </div>
        <div className="lg:col-span-6">
          <HeroMediaCarousel media={heroMedia} fallback={site.hero_image_url}/>
        </div>
      </section>

      {/* Why Ponderr — single row */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="display text-3xl font-bold mb-5">{site.section_title_features || "Why Ponderr"}</h2>
        <SingleLineScrollTrack testid="track-features" itemClass="w-[320px] sm:w-[360px]">
          {featList.map((f,i)=>(
            <div key={f.feature_id||i} className={`nb-card overflow-hidden h-full min-h-[140px] ${f.bg_color||"bg-white"}`}>
              {f.image_url && <img src={f.image_url} alt="" className="h-32 w-full object-cover border-b-2 border-black"/>}
              <div className="p-4">
                <div className="nb-label">FEATURE {i+1}</div>
                <h3 className="display text-lg font-bold mt-1 break-words line-clamp-2">{f.title}</h3>
                <p className="text-sm mt-1 break-words line-clamp-2">{f.description}</p>
              </div>
            </div>
          ))}
        </SingleLineScrollTrack>
      </section>

      <DiscoveryRails/>

      {/* Tabbed onboarding (replaces long register block) */}
      <TabbedOnboarding/>

      {/* Partner Schools — single row */}
      <section id="schools" className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="display text-3xl font-bold mb-2">{site.section_title_schools || "Our Partner Schools"}</h2>
        <p className="text-sm pf-muted mb-5">Schools powered by Ponderr. Click any card to visit their portal.</p>
        {schools.length ? (
          <SingleLineScrollTrack testid="track-schools" itemClass="w-80">
            {schools.map(s=>(
              <div key={s.slug} className="nb-card overflow-hidden h-full" data-testid={`school-card-${s.slug}`}>
                <Link to={`/school/${s.slug}`} className="block">
                  <img src={s.hero_image_url || FALLBACK_FEATURES[0].image_url} alt={s.name} className="w-full h-40 object-cover border-b-2 border-black"/>
                  <div className="p-4 pb-2">
                    <h3 className="display text-xl font-bold">{s.name}</h3>
                    <div className="text-xs text-slate-600 flex items-center gap-1 mt-1"><MapPin size={12}/>{s.address}</div>
                  </div>
                </Link>
                <div className="px-4 pb-3">
                  <Link to={`/school/${s.slug}`} className="nb-btn mt-1 inline-flex px-3 py-1 text-xs">Visit portal <ArrowRight size={12}/></Link>
                </div>
              </div>
            ))}
          </SingleLineScrollTrack>
        ) : <div className="nb-card p-8 text-center pf-muted">No partner schools yet</div>}
      </section>

      {/* Global Updates — single row */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="display text-3xl font-bold mb-5">{site.section_title_updates || "Global Updates"}</h2>
        {updates.length ? (
          <SingleLineScrollTrack testid="track-updates" itemClass="w-72">
            {updates.map((u,i)=>{
              const pt = (u.post_type && u.post_type!=="update") ? u.post_type : u.kind;
              const isLive = pt==="webinar"||pt==="live_class";
              const ICON = isLive?Calendar:u.kind==="quiz"?Trophy:FileText;
              const bg = isLive?"bg-[#93C5FD]":u.kind==="quiz"?"bg-[#FDE047]":"bg-[#C4B5FD]";
              const label = (pt||"news").replace("_"," ").toUpperCase();
              return <div key={u.update_id||i} className={`nb-card p-5 h-full ${bg}`}>
                <div className="flex items-center gap-2"><ICON size={18}/><span className="nb-label">{label}</span></div>
                <h3 className="display text-lg font-bold mt-2">{u.title}</h3>
                <p className="text-sm mt-1">{u.body}</p>
                {u.start_at && <div className="text-xs mono mt-2">🕒 {new Date(u.start_at).toLocaleString()}</div>}
                {u.link && <a href={u.link} target="_blank" rel="noreferrer" className="nb-btn mt-3 inline-flex px-3 py-1 text-xs">Open <ArrowRight size={12}/></a>}
              </div>;
            })}
          </SingleLineScrollTrack>
        ) : <div className="nb-card p-8 text-center pf-muted">No updates yet</div>}
      </section>

      {/* Tabbed plans */}
      <TabbedPlans/>

      {/* Latest Updates & Announcements — platform posts carousel */}
      <HorizontalPostCarousel
        title="Latest Updates & Announcements"
        subtitle="Fresh from Ponderr HQ"
        themeColor="indigo"
        posts={platformPosts}
        testid="global-posts-carousel"
      />

      {/* Contact + Subscribe (equal-width side-by-side) */}
      <section id="contact" className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-6 items-stretch">
          <div className="nb-card p-8 pf-accent-bg h-full flex flex-col">
            <h2 className="display text-2xl sm:text-3xl font-bold mb-3">Contact Us</h2>
            <form onSubmit={submitEnquiry} className="grid md:grid-cols-2 gap-3 flex-1">
              <select className="nb-select md:col-span-2" required value={contact.enquiry_type} onChange={e=>setContact({...contact,enquiry_type:e.target.value})} data-testid="enq-type">
                <option value="" disabled>Select Enquiry Type…</option>
                {ENQUIRY_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
              <input className="nb-input" placeholder="Name" required value={contact.name} onChange={e=>setContact({...contact,name:e.target.value})} data-testid="enq-name"/>
              <input className="nb-input" placeholder="Email" type="email" required value={contact.email} onChange={e=>setContact({...contact,email:e.target.value})} data-testid="enq-email"/>
              <input className="nb-input" placeholder="Phone" required value={contact.phone} onChange={e=>setContact({...contact,phone:e.target.value})} data-testid="enq-phone"/>
              <textarea className="nb-textarea md:col-span-2" rows={4} placeholder="Tell us about your needs" required value={contact.message} onChange={e=>setContact({...contact,message:e.target.value})} data-testid="enq-message"/>
              <button className="nb-btn md:col-span-2 justify-center mt-auto" data-testid="enq-submit">Send Enquiry <ArrowRight size={16}/></button>
            </form>
          </div>
          <SubscribeCard />
        </div>
      </section>

      <GlobalAppFooter site={site}/>
    </div>
  );
}
